import { React, useState, useEffect } from 'react';
import getBackend from 'backend/backend';
import { useTranslation } from "react-i18next";
import { Table, Container, Alert, Button } from 'react-bootstrap';
import SidebarPage from 'components/sidebar_page';
import { Paths } from 'paths';
import { useNavigate } from 'react-router-dom';
import { showConfirmation } from 'components/misc';

const ApiKeys = () => {
    const [t] = useTranslation('common');
    const [tokens, setTokens] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [updateSeqNo, setUpdateSeqNo] = useState(0);
    const [models, setModels] = useState([]);
    const navigate = useNavigate();
    const baseUrl = window.location.origin + "/api/llm/v1";

    useEffect(() => {
        let canceled = false;

        const fetchWorkers = async () => {
            try {
                const result = await getBackend().getWorkers();
                if (!canceled && result.error == null) {
                    const runnableWorkers = result.response.workers.filter(worker => worker.state === "running");
                    const uniqueModels = [...new Set(runnableWorkers.map(worker => worker.model))];
                    setModels(uniqueModels);

                } else if (!canceled) {
                    setError(result.error);
                }
            } catch (err) {
                if (!canceled) setError(err.message);
            } finally {
                if (!canceled) setLoading(false);
            }
        };

        const fetchTokens = async () => {
            try {
                const result = await getBackend().listApiTokens();
                if (!canceled && result.error == null) {
                    setTokens(result.response.tokens);
                } else if (!canceled) {
                    setError(result.error);
                }
            } catch (err) {
                if (!canceled) setError(err.message);
            } finally {
                if (!canceled) setLoading(false);
            }
        };

        fetchTokens();
        fetchWorkers();

        return () => {
            canceled = true;
        };
    }, [updateSeqNo]);

    return (
        <SidebarPage title={t('API Keys')} description={t('API Keys')}>
            <Container className="my-5">

                <h2 className="mb-4">{t('API Keys')}</h2>
                {loading &&
                    <div>{t('Loading...')}</div>
                }
                {error &&
                    <Alert variant="danger">{t('Error')}: {error}</Alert>
                }
                {models.length === 0 &&
                    <div>
                        <h3>{t('No running models available')}</h3>
                        <Button className="custom-button" variant="light" onClick={() => {
                            navigate(Paths.AddWorker.path);
                        }}>
                            {t('Run a model')}
                        </Button>
                    </div>
                }
                {models.length > 0 &&
                <>
                <Table striped bordered hover>
                    <thead>
                        <tr>
                            <th>{t('Key')}</th>
                            <th>{t('Created')}</th>
                            <th>{t('Actions')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {tokens.map((token) => (
                            <tr key={token.value}>
                                <td>{token.value}</td>
                                <td>{token.created}</td>
                                <td>
                                    <Button
                                        variant="danger"
                                        size="sm"
                                        onClick={async () => {
                                            let result = await showConfirmation("Delete api key", "Are you really going to delete the api key?");
                                            if (result.isConfirmed) {
                                                const result = await getBackend().deleteApiToken(token.value);
                                                if (result.error != null) {
                                                    setError(result.error);
                                                } else {
                                                    setUpdateSeqNo(updateSeqNo + 1);
                                                }
                                            }
                                        }}
                                    >
                                        {t('Delete')}
                                    </Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>

                <Button
                    className="custom-button"
                    variant="light"
                    onClick={async () => {
                        setError(null);

                        const result = await getBackend().createApiToken();
                        if (result.error != null) {
                            setError(result.error);
                        }
                        setUpdateSeqNo(updateSeqNo + 1);
                    }}
                >
                    {t('Create')}
                </Button>

                {tokens.length > 0 &&
                <div className="mt-5">
                    <h3>{t('API Usage Examples')}</h3>
                    <p>{t('You can use your API key to access the LocalIQ LLM API with OpenAI-compatible clients.')}</p>
                    <p>
                        {t('Replace apiKey with one of your API keys from the table above.')}
                    </p>
                    <p>
                        {t('Replace model with one of the running')} <a href={Paths.Workers.path}>{t('models')}</a> {t('from the table above.')}
                    </p>
                    <p>
                        {t('Replace baseURL with the URL of the LocalIQ API: ' + baseUrl + '.')}
                    </p>
                    <div className="bg-light p-3 border rounded">
                        <h5>{t('Python Example')}</h5>
                        <pre className="mb-0">
                            <code>
{`from openai import OpenAI

client = OpenAI(
    api_key="${tokens[0].value}",
    base_url="${baseUrl}"
)

response = client.chat.completions.create(
    model="${models[0]}",
    messages=[
        {"role": "system", "content": "You are a helpful assistant."},
        {"role": "user", "content": "How many people live in the world?"}
    ]
)

print(response.choices[0].message.content)`}
                            </code>
                        </pre>
                    </div>

                    <div className="bg-light p-3 border rounded mt-3">
                        <h5>{t('JavaScript Example')}</h5>
                        <pre className="mb-0">
                            <code>
{`import OpenAI from 'openai';

const openai = new OpenAI({
  apiKey: '${tokens[0].value}',
  baseURL: '${baseUrl}'
});

async function main() {
  const completion = await openai.chat.completions.create({
    model: '${models[0]}',
    messages: [
      { role: 'system', content: 'You are a helpful assistant.' },
      { role: 'user', content: 'How many people live in the world?' }
    ]
  });

  console.log(completion.choices[0].message.content);
}

main();`}
                            </code>
                        </pre>
                    </div>

                </div>
                }
                </>
                }
            </Container>
        </SidebarPage>
    );
};

export default ApiKeys;
